import type { Opportunity } from '@carboncopyco/types';
import { RequiredAsterisk, TextCaption } from '@carboncopyco/ui';
import { Combobox } from '@headlessui/react';
import { useField } from 'formik';
import {
  type ChangeEventHandler,
  type FocusEvent,
  type ReactNode,
  forwardRef,
} from 'react';
import { ValidationErrorMessages } from '../../consts';

type OpportunityInputProps = {
  handleChange: ChangeEventHandler<HTMLInputElement>;
  logo: ReactNode;
};

export const OpportunityInput = forwardRef<
  HTMLInputElement,
  OpportunityInputProps
>(({ handleChange, logo }, ref) => {
  const [opportunityField, meta] = useField({
    name: 'opportunity',
    validate: (value: Opportunity | null) => {
      if (!value?.name) {
        return ValidationErrorMessages.required;
      }
      return;
    },
  });

  return (
    <>
      <div className="flex">
        <Combobox.Label>
          <TextCaption
            label="Opportunity:"
            color="text-ds-natural-600"
          />
        </Combobox.Label>
        <RequiredAsterisk />
      </div>
      <div className="rounded border focus-within:border-ds-primary flex justify-between items-center py-2 px-2.5">
        <Combobox.Input
          ref={ref}
          onBlur={(event: FocusEvent<HTMLInputElement>) =>
            opportunityField.onBlur({
              ...event,
              target: {
                ...event.target,
                name: 'opportunity',
              },
            })
          }
          placeholder="Acme, inc."
          className="placeholder:text-ds-natural grow border-0 focus:ring-0 bg-transparent p-0"
          onChange={handleChange}
          displayValue={(opportunity: Opportunity) => opportunity?.name || ''}
          autoComplete="off"
        />
        {logo}
      </div>
      {meta.error && meta.touched && (
        <div className="mx-1 -bottom-5 absolute w-full">
          <div className="whitespace-nowrap">
            <TextCaption
              label={ValidationErrorMessages.required}
              color="text-ds-danger-400"
              lineHeight="leading-ds-100"
            />
          </div>
        </div>
      )}
    </>
  );
});
OpportunityInput.displayName = 'OpportunityInput';
